import { StoreBrandFiles } from '../../../schemas/storeGetStartedSchema';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { uploadUserProfileImages } from '../uploadUserProfileImages';
import { FilePurpose } from '../../../libs/interfaces/filePurpose';
import useIonicToast from '../../../hooks/useIonicToast';
import { usePageLoader } from '../../../context/pageLoader';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  authToken?: string;
};

// The existing profile image is rendered via `<img>` inside FileDropBox using
// the `existingPreviewUrl` prop — no need to fetch it into a File here. The
// bucket also lacks CORS headers for fetch().
export default function useProfileImageUploadService({ authToken }: Props) {
  const formMethod = useForm<z.infer<typeof StoreBrandFiles>>();
  const { presentIonToast } = useIonicToast();
  const { setBusy } = usePageLoader();
  const clientQuery = useQueryClient();
  const invalidateProfile = () =>
    clientQuery.invalidateQueries({ queryKey: ['user-profile'] });

  const handleFormSubmission = formMethod.handleSubmit(
    async ({ store_logo }) => {
      if (!authToken) return;
      try {
        setBusy(true);
        const files = [
          {
            title: 'profile_image' as const,
            file_purpose: FilePurpose.PROFILE_IMAGE,
            file: store_logo,
            requires_image: true,
          },
        ];
        await uploadUserProfileImages({
          authToken,
          files,
        });
        presentIonToast('Profile updated.');
        invalidateProfile();
      } catch (error: any) {
        presentIonToast(error?.message, 'error');
      } finally {
        setBusy(false);
      }
    }
  );

  return {
    formMethod,
    handleFormSubmission,
  };
}
